import * as React from 'react';
import { useGetClientCompaniesQuery } from 'src/services/api/companiesApi';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import {
  Button,
  MenuItem,
  MenuList,
  Popover,
  useTheme,
} from '@material-ui/core';
import { Avatar, Body, Heading, Icon } from 'copilot-design-system';
import { Skeleton } from '@material-ui/lab';
import { Company } from 'src/constants';
import { useLogout } from 'src/hooks/useLogout';
import { PortalConfigContext } from 'src/context';
import { setActiveCompanyId } from 'src/store/activeCompany/slice';

export const MobileCompanySwitcher = () => {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const dispatch = useAppDispatch();
  const { SignoutRedirectURL } = React.useContext(PortalConfigContext);

  const { data: companies = [], isLoading } = useGetClientCompaniesQuery();
  const activeClientCompanyId = useAppSelector(
    (state) => state.activeCompany.activeCompanyId,
  );
  const anchorEl = React.useRef<HTMLButtonElement>(null);
  const handleLogout = useLogout();

  /**
   * Handles the click event for the company switcher.
   * @param company - The company to set as active.
   */
  const handleClick = (company: Company) => () => {
    setOpen(false);
    dispatch(setActiveCompanyId(company.id));
  };

  const selectedCompany = React.useMemo(() => {
    return companies.find((company) => company.id === activeClientCompanyId);
  }, [companies, activeClientCompanyId]);

  React.useEffect(() => {
    // if there are companies and no active company, set the first company as active
    // TODO: this is a temporary solution, we will discuss what should be the default company
    // either the last active company or the first company in the list.
    if (companies.length > 0 && !activeClientCompanyId) {
      dispatch(setActiveCompanyId(companies.at(0)?.id || null));
    }
  }, [companies, activeClientCompanyId]);

  return (
    <div>
      <Button
        className="min-w-full flex items-center justify-start py-[10px] px-2 hover:bg-primary-hover"
        ref={anchorEl}
        onClick={() => setOpen(true)}
        style={{
          // TODO: remove this once we integrate the theme in tailwind
          backgroundColor: theme.palette.primary.dark,
        }}
      >
        {isLoading && <Skeleton width={100} height={20} />}

        {selectedCompany && (
          <>
            <Avatar
              className="mr-2 block sm:hidden"
              src={selectedCompany?.fields.avatarImageURL || ''}
              size="sm"
              fallbackColor={selectedCompany?.fields.fallbackColor}
              alt={selectedCompany?.fields.name || ''}
              variant="rounded"
              text={selectedCompany.fields.name.slice(0, 1)}
            />
            <Body className="text-white truncate" size="sm">
              {selectedCompany?.fields.name}
            </Body>
            <Icon
              icon={open ? 'ChevronUp' : 'ChevronDown'}
              className={'w-[12px] h-[12px] ml-[6px] text-white'}
            />
          </>
        )}
      </Button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        classes={{
          paper: 'w-[240px] mt-[2px] ml-[-8px] shadow-dropdown-menu',
        }}
      >
        <MenuList className="p-0">
          {companies.map((company) => (
            <MenuItem
              key={company.id}
              onClick={handleClick(company)}
              className="h-8 p-3"
            >
              <div className="flex items-center gap-2">
                <Avatar
                  src={company.fields.avatarImageURL || ''}
                  size="sm"
                  fallbackColor={company.fields.fallbackColor}
                  alt={company.fields.name}
                  variant="rounded"
                  text={company.fields.name.slice(0, 1)}
                />
                <Heading className="text-[14px]">{company.fields.name}</Heading>
              </div>
            </MenuItem>
          ))}
          <div className="h-[1px] bg-secondary-hover" />
          <MenuItem
            className="h-8 p-3"
            onClick={() => handleLogout(false, SignoutRedirectURL, true)}
          >
            <Body className="text-[14px]">Log out</Body>
          </MenuItem>
        </MenuList>
      </Popover>
    </div>
  );
};
